import { useReturnStore } from '@src/stores/returnStore';
import { isROSEnabled } from '@src/constants/fs-values';

/**
 * The encrypted token the return is currently submitted under.
 *
 * Every call that has to prove what the customer was approved to send back
 * reads it from here, so there is one answer to "which token is current"
 * rather than one per call site.
 *
 * **Read it at the moment you need it — never copy it into state or a variable
 * that outlives the call.** Whichever service issues the token can reissue it
 * partway through, and deriving it on every read is what makes the newest one
 * the one that travels: a reissue takes effect with no other action, and an
 * undone change takes its token back with it. A value captured earlier keeps
 * sending a token for a return the customer no longer has.
 *
 * It also has to be read through `getState()` rather than a hook, because the
 * submit path mutates the store and reads it back inside a single synchronous
 * tick — a value destructured during render is already stale by then.
 */
export const getCurrentReturnET = (): string => {
  const { returnMethodsResponse, policyValidatorResponse } =
    useReturnStore.getState();

  // Off the migration switch the return methods call never runs, and the policy
  // validator is the only thing that issues a token.
  if (!isROSEnabled()) {
    return policyValidatorResponse?.et || '';
  }

  // On the switch both calls issue one, and the validator's is the newer of the
  // two wherever it exists: it runs after the methods call, and both run again
  // on every review-screen edit — in that order, so the validator still answers
  // last. It is also the only one carrying the amendment made for returns that
  // qualify to be kept, which has to agree with the keep-it flag sent alongside
  // it.
  //
  // The methods token is the fallback rather than the preference, and that is
  // deliberate for this phase rather than an oversight. It becomes the only
  // token available the moment the validator stops issuing one, so a return
  // that never reaches the validator — as an in-store-only return will once
  // the validator is narrowed — keeps a token without this needing to change.
  //
  // Flip the order once the validator has stopped issuing tokens and the
  // create call checks the methods one instead.
  //
  // The precondition that used to be recorded here has been met: the edit path
  // re-requests the return methods, so the methods token is no longer the one
  // predating the customer's last edit. Nothing about the flip is blocked from
  // this side any more — what is left is the create call reading the methods
  // token, which is the other end of the same change. Until that lands the
  // order below is simply the one that works either way, rather than the one
  // holding something up.
  return policyValidatorResponse?.et || returnMethodsResponse?.et || '';
};
